import { FC } from 'react';

import { Badge, Button, CtaLink, HeadingTagType, PromotionCardProps } from '@purpur/library';
import { Icon } from '@purpur/library/icon';
import { arrowRight } from '@purpur/library/icon/assets/arrow-right';
import { checkmark } from '@purpur/library/icon/assets/checkmark';

import { getSvgIcon } from '@telialt/common-icon/src';
import { InlineRichText } from '@telialt/common-rich-text-content/src/InlineRichText';
import { RichTextContent } from '@telialt/common-rich-text-content/src/RichTextContent';

import { ImageLink } from '../common/ImageLink';
import { CtaProvider } from '../CtaProvider/CtaProvider';
import {
    DescriptionList,
    DescriptionListItemParagraph,
    ImageCtasContainer,
    PromoCardBadgeWrapper,
    PromoCardContent,
    Slot,
    StyledPromotionCard,
    StyledResponsiveImage,
} from './BlockPromo.styles';
import { PromoCardData } from './model';
import { PromoCardImage } from './PromoCardImage';

type Props = {
    card: PromoCardData;
    titleTag: HeadingTagType;
    itemIndex: number;
};

export const PromoCard: FC<Props> = ({ card, titleTag, itemIndex }) => {
    const {
        variant,
        ctas,
        imageContainerReference,
        media,
        title,
        badge,
        badgeIcon,
        description,
        descriptionList,
        offerSchema,
        legalText,
        contentSlot,
    } = card;

    const actions: PromotionCardProps['actions'] = (
        <>
            {ctas
                ?.filter((cta) => !cta.image)
                .map((cta, index) => (
                    <CtaProvider cta={cta} key={index}>
                        {({ href, target, rel, text, onClick }) =>
                            href ? (
                                <CtaLink
                                    variant={index === 0 ? 'primary' : 'secondary'}
                                    href={href}
                                    target={target}
                                    rel={rel}
                                    onClick={onClick}
                                    negative={variant === 'primary'}
                                    data-element="Button"
                                    data-element-name={text}
                                    data-element-type={index === 0 ? 'Primary' : 'Secondary'}
                                >
                                    {text}
                                </CtaLink>
                            ) : (
                                <Button
                                    variant={index === 0 ? 'primary' : 'secondary'}
                                    onClick={onClick}
                                    negative={variant === 'primary'}
                                    data-element="Button"
                                    data-element-name={text}
                                    data-element-type={index === 0 ? 'Primary' : 'Secondary'}
                                >
                                    {text}
                                    <Icon svg={arrowRight} size="xs" />
                                </Button>
                            )
                        }
                    </CtaProvider>
                ))}
        </>
    );

    const imageCtas = ctas?.filter((cta) => cta.image);

    const image =
        media &&
        (imageContainerReference ? <PromoCardImage images={media} /> : <StyledResponsiveImage images={media} />);

    const additionalText = legalText ? (
        <InlineRichText richText={legalText} isNegative={variant === 'primary'} />
    ) : undefined;

    const slot = !!contentSlot && (
        <Slot>
            <RichTextContent richText={contentSlot} isNegative={variant === 'primary'} />
        </Slot>
    );

    const badgeElement = badge && (
        <PromoCardBadgeWrapper>
            <Badge variant={variant === 'primary' ? 'special' : 'attention'} showIcon={false}>
                {badgeIcon && <Icon svg={getSvgIcon(badgeIcon)} size="xs" />}
                {badge}
            </Badge>
        </PromoCardBadgeWrapper>
    );

    return (
        <>
            <StyledPromotionCard
                variant={variant}
                title={title}
                titleTag={titleTag}
                beforeTitle={badgeElement}
                enableTitleHyphenation={false}
                decorativeImage={image}
                actions={actions}
                additionalText={additionalText}
                data-section="Promo Card"
                data-section-name={title}
                data-section-color-option={variant}
                data-section-index={itemIndex + 1}
            >
                <PromoCardContent>
                    <RichTextContent richText={description} isNegative={variant === 'primary'} />
                    {descriptionList?.length > 0 && (
                        <DescriptionList>
                            {descriptionList.map((listItem, index) => (
                                <li key={index}>
                                    <DescriptionListItemParagraph>
                                        <Icon svg={checkmark} size="xs" />
                                        {listItem}
                                    </DescriptionListItemParagraph>
                                </li>
                            ))}
                        </DescriptionList>
                    )}
                </PromoCardContent>
                {slot}
                {imageCtas?.length > 0 && (
                    <ImageCtasContainer>
                        {imageCtas.map((cta, index) => (
                            <ImageLink key={index} cta={cta} />
                        ))}
                    </ImageCtasContainer>
                )}
            </StyledPromotionCard>
            {!!offerSchema && (
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(offerSchema) }} />
            )}
        </>
    );
};
